import {
  AccountMin,
  AssetId,
  ContentId,
  DocumentAccessRequest,
  DocumentId,
  GetGraphQuery,
  MailSubscriptionId,
  SARRequest,
  SubscribeRequest,
} from "platformed-client/types";
import {platformedClient} from "..";
import {invalidateQueries, q} from "../../state";
import {getTrustCenterPassword} from "../../TrustCenter/hooks/accessPassword";

function basicAuth(account: AccountMin, overridePassword?: string): {Authorization: `Basic ${string}`} {
  const password = overridePassword ?? getTrustCenterPassword(account.account_id);
  const token = btoa(`${account.account_id}:${password}`);
  return {Authorization: `Basic ${token}`};
}

function basicAuthUrl(account: AccountMin, url: string): string {
  const urlObj = new URL(url);
  const auth = basicAuth(account).Authorization;
  if (auth) {
    urlObj.searchParams.append("auth", auth);
  }
  return urlObj.toString();
}

export async function listDocuments(account: AccountMin) {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/documents`,
    {path: {region: account.region, account_id: account.account_id}, header: basicAuth(account)},
  );
}

export const downloadDocument = async (account: AccountMin, documentId: DocumentId) => {
  const a = document.createElement("a");
  const url = platformedClient.externalUrl(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/documents/{document_id}/download`,
    {path: {region: account.region, account_id: account.account_id, document_id: documentId}},
  );
  a.href = basicAuthUrl(account, url);
  a.click();
};

export async function requestDocumentAccess(account: AccountMin, documentRequest: DocumentAccessRequest) {
  return await platformedClient.externalRequest(
    "post",
    `/region/{region}/external_api/accounts/{account_id}/showcase/documents/request_access`,
    {path: {region: account.region, account_id: account.account_id}, body: documentRequest, header: basicAuth(account)},
  );
}

export const getAssetUrl = (account: AccountMin, assetId: AssetId) => {
  const url = platformedClient.externalUrl(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/assets/{asset_id}/download`,
    {path: {region: account.region, account_id: account.account_id, asset_id: assetId}},
  );
  return basicAuthUrl(account, url);
};

export const getContent = async (account: AccountMin, contentId: ContentId) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/contents/{content_id}`,
    {path: {region: account.region, account_id: account.account_id, content_id: contentId}, header: basicAuth(account)},
  );
};

export const getConfig = async (account: AccountMin, overridePassword?: string) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/config`,
    {path: {region: account.region, account_id: account.account_id}, header: basicAuth(account, overridePassword)},
  );
};

export const getFaq = async (account: AccountMin) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/faq`,
    {path: {region: account.region, account_id: account.account_id}, header: basicAuth(account)},
  );
};

export const getAiFaq = async (account: AccountMin) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/ai_faq`,
    {path: {region: account.region, account_id: account.account_id}, header: basicAuth(account)},
  );
};

export const getGraph = async (account: AccountMin, query: GetGraphQuery) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/graph`,
    {path: {region: account.region, account_id: account.account_id}, query: query, header: basicAuth(account)},
  );
};

export const getSchema = async (account: AccountMin) => {
  return await platformedClient.externalRequest(
    "get",
    `/region/{region}/external_api/accounts/{account_id}/showcase/schema`,
    {path: {region: account.region, account_id: account.account_id}, header: basicAuth(account)},
  );
};

export async function subscribe(account: AccountMin, subscribeRequest: SubscribeRequest) {
  const res = await platformedClient.externalRequest(
    "post",
    `/region/{region}/external_api/accounts/{account_id}/showcase/subscribe`,
    {
      path: {region: account.region, account_id: account.account_id},
      body: subscribeRequest,
      header: basicAuth(account),
    },
  );
  await invalidateQueries([q.vendorToolkit.trustCenterMailingList.filter()]);
  return res;
}

export async function verifySubscriptionEmail(
  account: AccountMin,
  mailSubscriptionId: MailSubscriptionId,
  token: string,
) {
  return await platformedClient.externalRequest(
    "post",
    `/region/{region}/external_api/mail_subscription/{mail_subscription_id}/verify`,
    {
      path: {region: account.region, mail_subscription_id: mailSubscriptionId},
      header: {Authorization: `Bearer ${token}`},
    },
  );
}

export async function sarRequest(account: AccountMin, sarRequest: SARRequest) {
  return await platformedClient.externalRequest(
    "post",
    `/region/{region}/external_api/accounts/{account_id}/showcase/sar`,
    {path: {region: account.region, account_id: account.account_id}, body: sarRequest, header: basicAuth(account)},
  );
}
